/**
 * Focus panel — appears next to the side panel when the player has
 * focus-locked an enemy. Renders the enemy's limb silhouette with the
 * drifting reticle overlay + the sim-side metadata (state, awareness,
 * mark).
 *
 * The window auto-shows on focus and auto-hides when focus drops.
 */

import type { Guard } from '../sim/guard';
import type { Player } from '../sim/player';
import { BODY_LAYOUT, computeReticulePosition, limbCenter } from '../sim/reticule';
import { renderLimbSilhouette } from './limbSilhouette';
import type { GameWindow } from './window';

export class FocusPanel {
  private readonly root: HTMLElement;
  private readonly window: GameWindow;
  private lastVisible = false;

  constructor(win: GameWindow) {
    this.window = win;
    this.root = document.createElement('div');
    this.root.className = 'fp-root';
    this.window.body.appendChild(this.root);
    // Hidden by default — focus lock brings it on. Set display directly (not via
    // setVisible, whose lastVisible===false short-circuit would no-op here).
    this.window.root.style.display = 'none';
    this.lastVisible = false;
  }

  private setVisible(v: boolean): void {
    if (this.lastVisible === v) return;
    this.lastVisible = v;
    this.window.root.style.display = v ? '' : 'none';
  }

  update(player: Player, guards: readonly Guard[]): void {
    const focused = player.focusedTargetId !== null
      ? guards.find((g) => g.id === player.focusedTargetId)
      : undefined;
    if (!focused) {
      this.setVisible(false);
      return;
    }
    this.setVisible(true);
    // Match the sim's fire-path aim: if the player has arrow-key-selected
    // a limb, reticle sits at that limb's center. Otherwise it drifts.
    // Distance factor grows the drift radius so far shots wander more.
    const dx = focused.x - player.x;
    const dy = focused.y - player.y;
    const dist = Math.hypot(dx, dy);
    const w = player.weapons[player.activeWeaponIdx];
    const distFactor = w && !w.isMelee ? Math.max(0, Math.min(1, dist / Math.max(1, w.rangeTiles))) : 0;
    const reticule = player.selectedLimbIdx >= 0
      ? limbCenter(player.selectedLimbIdx)
      : computeReticulePosition(player.reticuleDriftT, distFactor);
    const limbLabel = player.selectedLimbIdx >= 0
      ? BODY_LAYOUT[player.selectedLimbIdx]!.part.toUpperCase()
      : 'DRIFT';
    const mark = focused.marked ? '★ MARKED' : '—';
    this.root.innerHTML = '';
    const label = `${focused.isJanitor ? 'JANITOR' : 'GUARD'} #${focused.id}`;
    this.root.appendChild(renderLimbSilhouette(focused.body, label, reticule));
    const meta = document.createElement('div');
    meta.className = 'sp-meta';
    meta.innerHTML = `
      <div class="sp-row"><span class="sp-label">aim</span><span class="sp-value">${limbLabel}</span></div>
      <div class="sp-row"><span class="sp-label">range</span><span class="sp-value">${dist.toFixed(1)}t</span></div>
      <div class="sp-row"><span class="sp-label">state</span><span class="sp-value">${focused.state}</span></div>
      <div class="sp-row"><span class="sp-label">mark</span><span class="sp-value">${mark}</span></div>
    `;
    this.root.appendChild(meta);
  }
}
